import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { House, CarProfile, Briefcase, ShieldCheck, User as UserIcon } from "@phosphor-icons/react";

// i18n keys are resolved AT RENDER time so switching language re-labels the
// tabs without a reload.
const ALL_TABS = [
  { to: "/", labelKey: "nav.home", icon: House, roles: ["all"] },
  { to: "/my-bookings", labelKey: "nav.myBookings", icon: CarProfile, roles: ["all"] },
  { to: "/company-bookings", labelKey: "nav.companyBookings", icon: Briefcase, roles: ["admin"] },
  { to: "/admin", labelKey: "nav.admin", icon: ShieldCheck, roles: ["admin"] },
  { to: "/account", labelKey: "nav.account", icon: UserIcon, roles: ["all"] },
];

export default function BottomTabBar() {
  const { user } = useAuth();
  const location = useLocation();
  const { t: tr, i18n } = useTranslation();
  const isEn = i18n.language === "en";

  const isAdmin = !!(user?.is_admin || user?.role === "admin");

  const tabs = useMemo(
    () => ALL_TABS.filter((t) => t.roles.includes("all") || (isAdmin && t.roles.includes("admin"))),
    [isAdmin],
  );

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <nav
      dir={isEn ? "ltr" : "rtl"}
      data-testid="bottom-tab-bar"
      className="fixed bottom-0 inset-x-0 z-40 border-t border-primary/25 bg-[#0A1936]/95 backdrop-blur-md"
      style={{ height: "64px", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {/* dir toggles automatically via language — DO NOT add flex-row-reverse */}
      <ul className="max-w-3xl mx-auto h-full flex flex-row items-stretch justify-between px-2">
        {tabs.map((t) => {
          const active = isActive(t.to);
          return (
            <li key={t.to} className="flex-1">
              <Link
                to={t.to}
                data-testid={`bottom-tab-${t.to.replace(/[^a-z]/gi, "") || "home"}`}
                className={`relative h-full flex flex-col items-center justify-center gap-1 transition-all ${
                  active ? "text-primary" : "text-[#9BA3B4] hover:text-white"
                }`}
              >
                <t.icon size={22} weight={active ? "fill" : "regular"} />
                <span className="text-[11px] font-semibold">{tr(t.labelKey)}</span>
                {active && (
                  <span className="absolute top-0 h-[3px] w-8 bg-primary rounded-b-full" aria-hidden />
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
